import { Button } from '@components/button';
import { arraysOverlap } from '@utils/arrays';
import { UserRoles } from '@models/user';
import { QuoteStatus, QuoteType } from '@models/quotes';
import { useStore } from '@store/GlobalStore';
import { shallow } from 'zustand/shallow';
import { useParams } from 'react-router-dom';
import invariant from 'tiny-invariant';
import { useQuote } from '@services/quotes';
import { ProductOfferingId } from '@models/newQuote';
import { PayAndBindButton } from '@pages/BindQuote/PayAndBindButton';
import { getFormattedPolicyNumber } from '@utils/policy';
import { getCoverageTypeText } from '@pages/BindQuote/getCoverageTypeText';
import { ConfirmationDialog } from '@components/dialog/ConfirmationDialog';
import { IConfirmDialog } from '@pages/QuoteDetails/types';
import { useEffect, useState } from 'react';
import { Tooltip } from '@components/tooltip';
import { useQuoteStore } from '@store/Quote/QuoteStore';
import { useGetCustomer } from '@services/customer';
import { useGetPolicy } from '@services/policy/policies';
import { isUnderlyingCoverageBindingRequirementUnmet } from '@pages/QuoteDetails/constants/QuoteDetails.constants';
import { isBrokerageBindBlocked } from '@pages/BindQuote/isBrokerageBindBlocked';

const GenericBrokeragePrefix = 'Generic Brokerage';

type BindQuoteHeaderButtonProps = {
  areBillingFieldsFilled: boolean;
  areDeclarationFieldsFilled: boolean;
  onOpenChangelog: () => void;
  onShowMultiPolicyBindDialog: (value: boolean) => void;
  onHandleBindQuote: () => void;
  onSaveQuote: ({ exitOnSave }: { exitOnSave?: boolean }) => void;
  canNavigateAway: boolean;
};

export const BindQuoteHeaderButtons = ({
  areBillingFieldsFilled,
  areDeclarationFieldsFilled,
  onOpenChangelog,
  onShowMultiPolicyBindDialog,
  onHandleBindQuote,
  onSaveQuote,
  canNavigateAway,
}: BindQuoteHeaderButtonProps) => {
  const [{ isLioSales, isInternalUser, isBroker, isCustomerUser }, roles] = useStore(
    (state) => [state.userAuth, state.roles],
    shallow,
  );
  const [isEndorsement, attributesById] = useQuoteStore(
    (state) => [state.isEndorsement, state.attributesById],
    shallow,
  );
  const { quoteId } = useParams<{ quoteId: string }>();
  const [confirmationDialogState, setConfirmationDialogState] = useState<IConfirmDialog | null>(null);
  invariant(quoteId);
  const { quote, refetch: refetchQuote } = useQuote(quoteId);

  // The quote's underlyingCoverageBindingRequirement gates bind, but useQuote's 5-minute staleTime
  // means it can still reflect a pre-bind state if the underlying quote/policy was bound moments ago
  // (e.g. via the CTA on this page) and the user returns within that window. Force a fresh read here,
  // but only for quotes that can have this requirement at all — continuedFromPolicyId is fixed at
  // quote creation, so it's safe to read off the possibly-stale cached quote to decide.
  const continuedFromPolicyId = quote?.continuedFromPolicyId;
  useEffect(() => {
    if (continuedFromPolicyId) {
      refetchQuote();
    }
  }, [continuedFromPolicyId, refetchQuote]);

  // Same staleTime concern for the brokerage bind gate: if the brokerage is (or was) in onboarding,
  // its lifecycleStatus can flip to Appointed while this quote sits cached. Refetch on mount so a user
  // returning to Review & Finalize after the brokerage is provisioned isn't stuck behind stale data.
  const brokerageLifecycleStatus = quote?.brokerage?.lifecycleStatus;
  useEffect(() => {
    if (brokerageLifecycleStatus === 'Onboarding') {
      refetchQuote();
    }
  }, [brokerageLifecycleStatus, refetchQuote]);

  const { structuredQuote } = useQuoteStore((state) => ({ structuredQuote: state.structuredQuote }), shallow);
  const { customer } = useGetCustomer(quote?.customer?.id || '');
  const { data: policy } = useGetPolicy({ policyId: quote?.policyId });

  if (!quote) {
    return null;
  }

  const isRenewal = quote.quoteType === QuoteType.Renewal;

  const bindPolicyAllowedRoles = [
    UserRoles.SystemAdmin,
    UserRoles.PortalAdmin,
    UserRoles.Underwriter,
    UserRoles.LIOContractor,
    UserRoles.CSR,
    UserRoles.Producer,
    UserRoles.Quoter,
  ];
  const showGenericBrokerageAlert = !!quote?.brokerage && quote.brokerage.name.startsWith(GenericBrokeragePrefix);
  const isUnderwritten = quote?.status.id === QuoteStatus.Underwritten.id;
  const isReadyToBind = quote?.status.id === QuoteStatus.ReadyToBind.id;
  const isBindEnabledForBroker = isBroker && isReadyToBind;
  const isUserRoleAllowedToBind = arraysOverlap(roles, bindPolicyAllowedRoles);

  const allowCustomerUserBind = isCustomerUser && ProductOfferingId.SpecialEvent === quote?.offering?.id;

  const customerIsNotConfirmedMatch = customer?.matchState !== 'Confirmed';
  let underlyingCoverageNotActive = false;
  let underlyingCoverageExpiresBeforeQuote = false;

  // if relatedPolicySources has an underlying coverage and it is not active or expired, disable bind
  // or it is active or expired but the current quote expires after the associated policy also disable bind
  if (quote.quoteType === QuoteType.Renewal && policy?.relatedPolicySources) {
    const underlyingCoverageRelation = policy.relatedPolicySources.find((relation) => {
      return relation.policyRelationType === 'UnderlyingCoverage';
    });

    if (underlyingCoverageRelation) {
      if (
        underlyingCoverageRelation.sourcePolicy.status !== 'Active' &&
        underlyingCoverageRelation.sourcePolicy.status !== 'Expired'
      ) {
        underlyingCoverageNotActive = true;
      } else {
        if (quote.expiresOn > new Date(underlyingCoverageRelation.sourcePolicy.policyTerm?.expiresOn || '1970-01-01')) {
          underlyingCoverageExpiresBeforeQuote = true;
        }
      }
    }
  }

  const underlyingCoverageBindingRequirement = quote.underlyingCoverageBindingRequirement;
  const underlyingCoverageBindingRequirementUnmet = isUnderlyingCoverageBindingRequirementUnmet(
    underlyingCoverageBindingRequirement,
  );

  // Server-authoritative bind gate mirrored on the front end (see isBrokerageBindBlocked); the API
  // enforces it regardless, so this is the matching UI block for all users.
  const brokerageNotApprovedToBind = isBrokerageBindBlocked(quote);

  const isBindPolicyDisabled =
    (!isInternalUser && !isBindEnabledForBroker && !allowCustomerUserBind) ||
    isLioSales ||
    showGenericBrokerageAlert ||
    brokerageNotApprovedToBind ||
    isUnderwritten ||
    !areBillingFieldsFilled ||
    (!isEndorsement && !areDeclarationFieldsFilled) ||
    (!isEndorsement && !isUserRoleAllowedToBind) ||
    Object.keys(attributesById).length !== 0 ||
    customerIsNotConfirmedMatch ||
    underlyingCoverageNotActive ||
    underlyingCoverageExpiresBeforeQuote ||
    underlyingCoverageBindingRequirementUnmet;

  const handleOnClick = () => {
    if (quote.createdFromPolicy) {
      const lineOfBusiness = structuredQuote ? getCoverageTypeText(structuredQuote) : '-';

      const policyNumber = getFormattedPolicyNumber(
        quote.createdFromPolicy.policyNumberPrefix,
        quote.createdFromPolicy.policyNumber.policyNumber,
      );

      setConfirmationDialogState({
        show: true,
        confirmText: 'Bind & Generate Policy',
        cancelText: 'Go Back',
        navigatePath: '',
        description: 'Bind Related Policy',
        secondaryDescription: `You are about to bind a new ${lineOfBusiness} Policy that will be linked to ${policyNumber}. Please ensure that a related policy of this type doesn’t already exist before binding.`,
        onConfirmation: () => {
          onHandleBindQuote();
        },
      });
      return;
    }

    if (isEndorsement || isRenewal) {
      onOpenChangelog();
      return;
    }

    if (quote?.multiPolicyQuoteDestinations.length > 0) {
      onShowMultiPolicyBindDialog(true);
      return;
    }
    onHandleBindQuote();
  };

  const showPayAndBindButton = quote.offering.requirePreBindPayment;
  // underlyingCoverageBindingRequirementUnmet's reason is surfaced via a persistent, focusable
  // Alert in BindQuoteHeader instead of here, since a tooltip on a disabled button is not
  // reachable by keyboard/screen reader users.
  const tooltipButtonMessage = isUnderwritten
    ? 'There are some questions on the quote that need answered before you can bind. Please go review your submission'
    : customerIsNotConfirmedMatch
      ? 'Bind is not available. Please reach out to your LIO Underwriter'
      : '';

  return [
    <Button
      key='save-button'
      disabled={isLioSales}
      variant='outlined'
      onClick={() => onSaveQuote({ exitOnSave: true })}
    >
      Save & Exit
    </Button>,
    showPayAndBindButton ? (
      <Tooltip key='pay-and-bind-button' title={tooltipButtonMessage}>
        <span>
          <PayAndBindButton
            quote={quote}
            isLoading={canNavigateAway}
            disabled={isBindPolicyDisabled || canNavigateAway}
            onHandleBindQuote={onHandleBindQuote}
            onSaveQuote={onSaveQuote}
          />
        </span>
      </Tooltip>
    ) : (
      <>
        <Tooltip key='proceed-button' title={tooltipButtonMessage}>
          <span>
            <Button
              disabled={isBindPolicyDisabled || canNavigateAway}
              variant='contained'
              onClick={handleOnClick}
              isLoading={canNavigateAway}
            >
              {isEndorsement || isRenewal ? 'View Final Change Summary' : 'Bind & Generate Policy'}
            </Button>
          </span>
        </Tooltip>
        <ConfirmationDialog
          key='confirmation-dialog-binding'
          isOpen={confirmationDialogState?.show ?? false}
          onCancel={() => setConfirmationDialogState(null)}
          description={confirmationDialogState?.description ?? ''}
          secondaryDescription={confirmationDialogState?.secondaryDescription}
          buttonConfig={{
            onConfirm: confirmationDialogState?.onConfirmation ?? (() => {}),
            confirmText: confirmationDialogState?.confirmText ?? '',
            secondaryText: confirmationDialogState?.cancelText ?? '',
          }}
        />
      </>
    ),
  ];
};
